import { useHandleAvailabilityQuery } from "Api/Queries/Social/useHandleAvailabilityQuery";
import { useDebounce } from "Hooks/useDebounce";
import { normalizeHandle } from "Schemas/handleSchema";

export type HandleAvailabilityState = {
  /** The availability check confirmed the CURRENT input is taken by someone else. */
  isTaken: boolean;
  /** The availability check confirmed the CURRENT input is free. */
  isAvailable: boolean;
  /** A check for the current input is in flight (drives the in-input spinner). */
  isChecking: boolean;
  /** The input equals the persona's existing handle — no check runs, submit can skip the save. */
  isUnchanged: boolean;
  normalizedInput: string;
};

/**
 * Debounced live availability for a handle input. Host forms call this once and share the
 * state between the field display (HandleField) and their submit gating; results are keyed
 * to the current input so stale responses never mislabel a handle.
 */
export const useHandleAvailabilityState = (
  rawValue: string,
  initialHandle?: string | null,
): HandleAvailabilityState => {
  const normalizedInput = normalizeHandle(rawValue ?? "");
  const debouncedHandle = useDebounce(normalizedInput, 300);

  const isUnchanged =
    !!initialHandle && normalizedInput === normalizeHandle(initialHandle);
  const { data: availability, isFetching } = useHandleAvailabilityQuery(
    isUnchanged ? "" : debouncedHandle,
  );

  const isChecked = availability?.normalizedHandle === normalizedInput;

  return {
    isTaken: isChecked && availability.isAvailable === false,
    isAvailable: isChecked && availability.isAvailable === true,
    isChecking: isFetching,
    isUnchanged,
    normalizedInput,
  };
};
